
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import WeChatModal from './WeChatModal';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const { t } = useTranslation();
  const [isWeChatModalOpen, setIsWeChatModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleContactClick = (type: string) => {
    if (type === 'email') {
      window.open('mailto:business@koluno.com', '_blank');
    } else if (type === 'telegram') {
      window.open('https://t.me/koluno2020', '_blank');
    } else if (type === 'wechat') {
      setIsWeChatModalOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://getform.io/f/amdywneb', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t('btn_contact_now')}
            </h3>
            <p className="text-gray-600">
              {t('contact_choose_method')}
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            <button
              onClick={() => handleContactClick('email')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-mail-line text-blue-600 text-lg"></i>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{t('contact_email')}</p>
                  <p className="text-sm text-gray-600">business@koluno.com</p>
                </div>
              </div>
              <i className="ri-external-link-line text-gray-400"></i>
            </button>

            <button
              onClick={() => handleContactClick('telegram')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-telegram-line text-blue-600 text-lg"></i>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{t('contact_telegram')}</p>
                  <p className="text-sm text-gray-600">@koluno2020</p>
                </div>
              </div>
              <i className="ri-external-link-line text-gray-400"></i>
            </button>

            <button
              onClick={() => handleContactClick('wechat')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-wechat-line text-green-600 text-lg"></i>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{t('contact_wechat')}</p>
                  <p className="text-sm text-gray-600">ljy520155</p>
                </div>
              </div>
              <i className="ri-qr-code-line text-gray-400"></i>
            </button>
          </div>

          <div className="border-t pt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">或者填写联系表单</h4>
            
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="邮箱地址"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="联系电话"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <input
                  type="text"
                  name="company"
                  placeholder="公司名称"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <textarea
                  name="message"
                  placeholder="请描述您的需求..."
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '提交中...' : '发送消息'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="text-green-600 text-center text-sm">
                  消息发送成功！我们会尽快回复您。
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="text-red-600 text-center text-sm">
                  发送失败，请稍后重试或直接联系我们。
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <WeChatModal 
        isOpen={isWeChatModalOpen} 
        onClose={() => setIsWeChatModalOpen(false)} 
      />
    </>
  );
}
